// SSEで受信したbase64 PCMチャンクを途切れなく順次再生するプレイヤー。
// AudioBufferSourceNodeをスケジュール再生する方式
// （参照: saleseed-ai-roleplay hooks/use-audio-player.ts。リングバッファ+ScriptProcessorから
//   非deprecatedなスケジュール方式に変更。同等の連続再生・完了待ちを提供する）

const resample = (input, ratio) => {
  if (ratio === 1) return input
  const outputLength = Math.floor(input.length * ratio)
  const output = new Float32Array(outputLength)
  for (let i = 0; i < outputLength; i++) {
    const srcIndex = i / ratio
    const srcInt = Math.floor(srcIndex)
    const frac = srcIndex - srcInt
    const a = input[srcInt] ?? 0
    const b = input[Math.min(srcInt + 1, input.length - 1)] ?? a
    output[i] = a + (b - a) * frac
  }
  return output
}

export class AudioPlayer {
  constructor() {
    this.audioContext = null
    this.scheduledTime = 0
    this.pendingSources = 0
    this.endResolvers = []
    this.sources = [] // スケジュール済み/再生中のソース（スキップ時に止めるため保持）
  }

  // ユーザー操作（クリック等）の後に呼ぶこと（ブラウザのAutoplay Policy）
  async init(sampleRate = 16000) {
    if (this.audioContext) return
    try {
      this.audioContext = new AudioContext({ sampleRate })
    } catch {
      // 16kHz非対応ブラウザはデフォルトレートで作成し、enqueue時にリサンプル
      this.audioContext = new AudioContext()
    }
    if (this.audioContext.state === "suspended") await this.audioContext.resume()
  }

  enqueue(base64, sampleRate = 16000) {
    if (!this.audioContext) return

    // base64 → Int16 → Float32（-1.0〜1.0）
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    const int16 = new Int16Array(bytes.buffer, 0, Math.floor(bytes.length / 2))
    let float32 = new Float32Array(int16.length)
    for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 32768

    // AudioContextのレートと異なる場合はリサンプル
    const contextRate = this.audioContext.sampleRate
    if (contextRate !== sampleRate) {
      float32 = resample(float32, contextRate / sampleRate)
    }
    if (float32.length === 0) return

    const buffer = this.audioContext.createBuffer(1, float32.length, contextRate)
    buffer.copyToChannel(float32, 0)

    const source = this.audioContext.createBufferSource()
    source.buffer = buffer
    source.connect(this.audioContext.destination)

    // 前チャンクの終了時刻に連結してスケジュール（途切れ防止）
    const now = this.audioContext.currentTime
    const startAt = Math.max(now + 0.02, this.scheduledTime)
    source.start(startAt)
    this.scheduledTime = startAt + buffer.duration

    this.sources.push(source)
    this.pendingSources++
    source.onended = () => {
      this.sources = this.sources.filter((s) => s !== source)
      this.pendingSources--
      if (this.pendingSources === 0) {
        this.endResolvers.forEach((resolve) => resolve())
        this.endResolvers = []
      }
    }
  }

  // 再生中のすべての音声が終わるまで待つ
  waitForEnd() {
    if (this.pendingSources === 0) return Promise.resolve()
    return new Promise((resolve) => this.endResolvers.push(resolve))
  }

  // 再生中/スケジュール済みの音声をすべて止め、完了待ちを即解決する（読み上げスキップ用）
  stop() {
    this.sources.forEach((source) => {
      source.onended = null // stop()で発火するendedによる二重処理を防ぐ
      try { source.stop() } catch { /* 既に終了済みなどは無視 */ }
    })
    this.sources = []
    this.pendingSources = 0
    this.scheduledTime = 0
    this.endResolvers.forEach((resolve) => resolve())
    this.endResolvers = []
  }

  get playing() {
    return this.pendingSources > 0
  }
}
